import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import L from 'leaflet';
import { FeatureGroup } from 'react-leaflet';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea'; // For description
// import { Checkbox } from '@/components/ui/checkbox'; // For device assignment list
// import { ScrollArea } from '@/components/ui/scroll-area'; // For device list
import { ArrowLeft, Save, MapPinned, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

import MapDraw from '../../../../components/map/map.component';
import EditControlWrapper from '../../../../components/map/EditControlWrapper';
import { saveGeofence, getAllGeofences, setActiveGeofence } from '../../../../redux/geofence/geofenceActions'; 
// We might need an updateGeofence action or saveGeofence should handle updates.
// Also, an action to fetch a single geofence by ID might be needed.

export default function GeofenceEditPage() {
  const { geofenceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEditMode = Boolean(geofenceId);

  const user = useSelector(state => state.user.currentUser);
  // Attempt to find the geofence if in edit mode
  const geofenceToEdit = useSelector(state => 
    isEditMode ? state.geofences.geofences.find(gf => gf._id === geofenceId) : null
  );
  // const devices = useSelector(state => state.assets.devices); // For device assignment

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3388ff');
  // const [assignedDevices, setAssignedDevices] = useState([]); // For device assignment
  const [geometry, setGeometry] = useState(null); // To store drawn/edited shape geometry
  const [mapInstance, setMapInstance] = useState(null);
  const featureGroupRef = useRef();

  useEffect(() => {
    // If in edit mode and geofence data is available, populate form
    if (isEditMode && geofenceToEdit) {
      setName(geofenceToEdit.name || '');
      setDescription(geofenceToEdit.properties?.description || '');
      setColor(geofenceToEdit.color || '#3388ff');
      setGeometry(geofenceToEdit.geometry || null); 
      // setAssignedDevices(geofenceToEdit.assignedDevices || []); // Assuming this structure
      
      // Draw the geofence on the map if geometry exists
      if (mapInstance && geofenceToEdit.geometry && featureGroupRef.current) {
        featureGroupRef.current.clearLayers();
        const layer = L.geoJSON(geofenceToEdit.geometry, {
          style: { color: geofenceToEdit.color || '#3388ff' }
        });
        featureGroupRef.current.addLayer(layer);
        // Fit map to bounds of the layer if possible
        try {
            if (layer.getBounds && Object.keys(layer.getBounds()).length > 0) {
                mapInstance.fitBounds(layer.getBounds());
            }
        } catch (e) { console.error("Error fitting bounds: ", e); }
      }
    } else if (!isEditMode) {
      // Reset form for new geofence
      setName('');
      setDescription('');
      setColor('#3388ff');
      setGeometry(null);
      // setAssignedDevices([]);
      if (featureGroupRef.current) featureGroupRef.current.clearLayers();
    }
    // Consider fetching all geofences if not available, or a single one by ID if geofenceToEdit is not found
    // if (!geofences || geofences.length === 0) dispatch(getAllGeofences(user.user._id, user.token));
  }, [isEditMode, geofenceToEdit, mapInstance, dispatch, user]);

  const handleMapReady = useCallback((map) => {
    setMapInstance(map);
    // Invalidate size shortly after map is ready
    setTimeout(() => map.invalidateSize(), 100);
  }, []);

  const onShapeCreated = (e) => {
    const layer = e.layer;
    const drawnGeoJSON = layer.toGeoJSON();
    setGeometry(drawnGeoJSON.geometry);
    // Add to featureGroupRef to show it on map immediately after drawing
    if(featureGroupRef.current) {
        featureGroupRef.current.clearLayers(); // Clear previous shapes if any during creation
        featureGroupRef.current.addLayer(L.geoJSON(drawnGeoJSON, { style: { color }}));
    }
  };

  const onShapeEdited = (e) => {
    e.layers.eachLayer(layer => {
      const editedGeoJSON = layer.toGeoJSON();
      setGeometry(editedGeoJSON.geometry);
    });
  };
  
  const onShapeDeleted = (e) => {
    setGeometry(null);
    if (featureGroupRef.current) featureGroupRef.current.clearLayers();
  };

  const handleSubmit = () => {
    if (!name) {
      alert('Geofence name is required.'); // Replace with Shadcn Toast or Alert
      return;
    }
    if (!geometry) {
      alert('Geofence shape is required. Please draw it on the map.');
      return;
    }

    const geofenceData = {
      _id: isEditMode ? geofenceId : undefined, // Include ID for updates
      name,
      color,
      geometry,
      properties: {
        description,
        // assignedDevices, // Include if implementing device assignment
      },
      userId: user.user._id,
    };
    // If saveGeofence handles both create & update based on _id, this is fine.
    // Otherwise, an updateGeofence action would be needed.
    dispatch(saveGeofence(geofenceData, user.token)).then(() => {
        dispatch(getAllGeofences(user.user._id, user.token)); // Refresh list
        navigate('/dashboard/fleet/geofence'); // Navigate back to geofence list view
    });
  };

  return (
    <div className="flex flex-col md:flex-row h-full gap-4 p-4 overflow-hidden">
      {/* Form Column */}
      <Card className="w-full md:w-1/3 lg:w-1/4 flex flex-col overflow-y-auto">
        <CardHeader>
          <CardTitle className="flex items-center">
            {isEditMode ? <Edit3 className="mr-2" /> : <MapPinned className="mr-2" />} 
            {isEditMode ? 'Edit Geofence' : 'Create New Geofence'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 flex-grow">
          <div>
            <Label htmlFor="geofence-name">Name</Label>
            <Input id="geofence-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Office Area" />
          </div>
          <div>
            <Label htmlFor="geofence-color">Color</Label>
            <Input id="geofence-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10" />
          </div>
          <div>
            <Label htmlFor="geofence-description">Description (Optional)</Label>
            <Textarea id="geofence-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Details about this geofence..." />
          </div>
          {/* Placeholder for Device Assignment UI */}
          {/* <div className="space-y-2">
            <Label>Assign Devices (Optional)</Label>
            <ScrollArea className="h-40 border rounded-md p-2">
              {devices.map(device => (
                <div key={device._id} className="flex items-center space-x-2 mb-1">
                  <Checkbox 
                    id={`device-${device._id}`}
                    checked={assignedDevices.includes(device._id)}
                    onCheckedChange={checked => {
                      setAssignedDevices(prev => 
                        checked ? [...prev, device._id] : prev.filter(id => id !== device._id)
                      );
                    }}
                  />
                  <Label htmlFor={`device-${device._id}`} className="font-normal">{device.deviceId} ({device.vehicle.plate})</Label>
                </div>
              ))}
            </ScrollArea>
          </div> */}
          <div className="flex-grow"></div> {/* Pushes buttons to bottom */}
          <div className="space-y-2 pt-4 border-t">
            <Button onClick={handleSubmit} className="w-full">
                <Save className="mr-2 h-4 w-4" /> {isEditMode ? 'Save Changes' : 'Create Geofence'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard/fleet/geofence')} className="w-full">
                Cancel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Map Column */}
      <Card className="w-full md:w-2/3 lg:w-3/4 flex-grow relative">
        {/* Tooltip/Instructions for map */}
        <div className="absolute top-2 left-2 z-10 bg-background/80 backdrop-blur-sm p-2 rounded-md shadow text-xs">
            <Info size={14} className="inline mr-1 mb-0.5"/>
            {isEditMode && geometry ? 'Click shape to edit. ' : ''}
            Use drawing tools to {!isEditMode ? 'create' : 'modify'} shape. Delete tool clears current drawing.
        </div>
        <MapDraw onMapReady={handleMapReady} style={{ height: '100%', width: '100%' }} className="rounded-md overflow-hidden">
          <FeatureGroup ref={featureGroupRef}>
            <EditControlWrapper
              position="topright"
              onCreated={onShapeCreated}
              onEdited={onShapeEdited}
              onDeleted={onShapeDeleted}
              draw={{rectangle: true, circle: true, polygon: true, marker: false, circlemarker: false, polyline: false}}
              edit={featureGroupRef.current ? { 
                featureGroup: featureGroupRef.current, 
                edit: {}, // Use empty object for default options instead of true
                remove: {}  // Use empty object for default options instead of true
              } : false} // Disable edit if featureGroup is not ready
            />
          </FeatureGroup>
        </MapDraw>
      </Card>
    </div>
  );
} 